import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { HelpCircle, XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface QuestionToolTipProps {
    title?: string
    description?: string
    children?: React.ReactNode
    className?: string
    iconClassName?: string
}

export const QuestionToolTip = ({
    title,
    description,
    children,
    className,
    iconClassName,
}: QuestionToolTipProps) => (
    <DialogPrimitive.Root modal={false}>
        <DialogPrimitive.Trigger asChild>
            <button
                type="button"
                aria-label={title ?? 'More information'}
                className={cn(
                    'text-muted-foreground hover:text-primary inline-flex cursor-pointer items-center justify-center transition-colors',
                    className,
                )}
            >
                <HelpCircle className={cn('size-4', iconClassName)} />
            </button>
        </DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
            <DialogPrimitive.Content
                className={cn(
                    'bg-card data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
                    'fixed inset-y-0 right-0 z-50 flex h-full w-[400px] flex-col gap-4 border-l shadow-lg',
                    'transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
                )}
                onPointerDownOutside={(e) => e.preventDefault()}
            >
                {(title || description) && (
                    <div className="flex flex-col gap-1.5 p-4 pr-12">
                        {title && (
                            <DialogPrimitive.Title className="text-foreground font-semibold">
                                {title}
                            </DialogPrimitive.Title>
                        )}
                        {description && (
                            <DialogPrimitive.Description className="text-muted-foreground text-sm">
                                {description}
                            </DialogPrimitive.Description>
                        )}
                    </div>
                )}
                {children && (
                    <div className="flex-1 overflow-y-auto px-4 pb-6 text-sm">{children}</div>
                )}
                <DialogPrimitive.Close
                    className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
                    aria-label="Close"
                >
                    <XIcon className="size-4" />
                </DialogPrimitive.Close>
            </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
)
